// QuizContext.js
import React, {createContext, useContext, useEffect, useState} from "react";
import Helpers from "../helpers/Helpers";
import adminQuizService from "../services/AdminQuizService";
import siteService from "../services/SiteService";
import {useNavigate} from "react-router-dom";

// Create a context
const QuizContext = createContext();

// Create a provider component
export function QuizProvider({ children, quizIn, isNew }) {
	const navigate = useNavigate();
	const [quiz, setQuiz] = useState(quizIn || {
		name: "",
		settings: {},
		image: "",
		description: "",
		steps: [],
	});
	const [isImageSelectOpen, setImageSelectOpen] = useState(false);

	const saveQuiz = async () => {
		let res = await adminQuizService.saveCreate(quiz);
		if (res) {
			setQuiz(res);
			navigate("/admin/quizer/"+res._id);
		}
	}
	const deleteQuiz = async () => {
		let res = await adminQuizService.delete(quiz._id);
		if (res) {
			navigate("/admin/quizer");
		}
	}

	const fetchQuiz = async (quizId) => {
		let req = await Helpers.searchPaginated('quiz',{_id: quizId});
		if (req.mainObj)
			setQuiz(req.mainObj[0]);
	}

	const getImageSrc = (imgName) => {
		if (quiz._id)
			return `${siteService.apiPath}/image/quiz/${quiz._id}/${imgName}`
	}


	useEffect(() => {
		if (isNew){
			saveQuiz();
		}
	}, [isNew]);

	const addStep = () => {
		setQuiz((prev) => ({
			...prev,
			steps: [
				...prev.steps,
				{ name: "", image: "", questions: [] }
			]
		}));
	};

	const removeStep = (index) => {
		setQuiz((prev) => ({ ...prev, steps: prev.steps.filter((step, i) => i !== index) }));
	};

	const handleQuizChange = (e) => {
		const { name, value } = e.target;
		setQuiz((prev) => ({ ...prev, [name]: value }));
	};

	const handleStepChange = (index, e) => {
		const { name, value } = e.target;
		const updatedSteps = [...quiz.steps];
		updatedSteps[index][name] = value;
		setQuiz((prev) => ({ ...prev, steps: updatedSteps }));
	};

	const addQuestion = (stepIndex, type) => {
		const updatedSteps = [...quiz.steps];
		updatedSteps[stepIndex].questions.push({
			text: "",
			type: type || "multipleChoice",
			answers: []
		});
		setQuiz((prev) => ({ ...prev, steps: updatedSteps }));
	};

	const removeQuestion = (stepIndex, questionIndex) => {
		const updatedSteps = [...quiz.steps];
		updatedSteps[stepIndex].questions.splice(questionIndex, 1);
		setQuiz((prev) => ({ ...prev, steps: updatedSteps }));
	};

	const handleQuestionChange = (stepIndex, questionIndex, e) => {
		const { name, value } = e.target;
		const updatedSteps = [...quiz.steps];
		updatedSteps[stepIndex].questions[questionIndex][name] = value;
		setQuiz((prev) => ({ ...prev, steps: updatedSteps }));
	};

	const addAnswer = (stepIndex, questionIndex) => {
		const updatedSteps = [...quiz.steps];
		updatedSteps[stepIndex].questions[questionIndex].answers.push({
			text: "",
			correct: false
		});
		setQuiz((prev) => ({ ...prev, steps: updatedSteps }));
	};

	const handleAnswerChange = (stepIndex, questionIndex, answerIndex, e) => {
		const { name, value, type, checked } = e.target;
		const updatedSteps = [...quiz.steps];
		updatedSteps[stepIndex].questions[questionIndex].answers[answerIndex][name] =
			type === "checkbox" ? checked : value;
		setQuiz((prev) => ({ ...prev, steps: updatedSteps }));
	};

	// Add a state to store the target path
	const [imageTargetPath, setImageTargetPath] = useState(null);

	// A utility function to set nested properties by path
	const setQuizPropertyByPath = (path, value) => {
		setQuiz((prev) => {
			const updatedQuiz = { ...prev };
			const keys = path.split('.');

			let current = updatedQuiz;

			// Loop through all keys, creating intermediate objects if necessary
			for (let i = 0; i < keys.length - 1; i++) {
				const key = keys[i];
				if (!(key in current)) {
					// Initialize as an object if the path does not exist
					current[key] = {};
				}
				current = current[key];
			}

			// Set the final property to the new value
			current[keys[keys.length - 1]] = value;

			return updatedQuiz;
		});
	};


// Function to handle image selection for different components
	const handleImageSelect = (image) => {
		if (imageTargetPath) {
			setQuizPropertyByPath(imageTargetPath, image);
			setImageSelectOpen(false);
			setImageTargetPath(null);  // Reset the path after setting
		}
	};


	return (
		<QuizContext.Provider
			value={{
				quiz,
				setQuiz,
				fetchQuiz,
				saveQuiz,
				deleteQuiz,
				getImageSrc,
				imageTargetPath,
				setImageTargetPath,
				handleImageSelect,
				isImageSelectOpen,
				setImageSelectOpen,
				addStep,
				removeStep,
				handleQuizChange,
				handleStepChange,
				addQuestion,
				removeQuestion,
				handleQuestionChange,
				addAnswer,
				handleAnswerChange,
				setQuizPropertyByPath,
			}}
		>
			{children}
		</QuizContext.Provider>
	);
}

// Create a custom hook to use QuizContext
export const useQuiz = () => useContext(QuizContext);
